import { useMutation } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import { type ReactNode, useEffect, useState } from 'react';
import { useUrlSearchParams } from 'use-url-search-params';

import { TEST_RECORD_SUCCEEDED_STATUS } from '../constants';
import { useAnalytics } from '../context/AnalyticsProvider';
import { useFeatures } from '../context/FeatureProvider';
import { parseCurrency } from '../entities/Currency';
import type { PaymentMethodType } from '../entities/enums';
import { CONFIRM_INVOICE_ORDER } from '../graphql';
import { useGenerateIdempotencyKey } from '../hook/useGenerateIdempotencyKey';
import { usePaymentsData } from '../hook/usePaymentsData';
import type { PaymentsForCajaProps } from '../library';
import translations from '../locales.vocab';
import type { HirerPaymentResult, PaymentStatus } from '../types';
import { checkIsPaymentsReturnUrl } from '../utils/checkUrlParams';
import { generatePaymentsViewDataForCaja } from '../utils/generatePaymentsViewData';
import { getPaymentMedium } from '../utils/getPaymentMedium';
import { getWithExpiry, setWithExpiry } from '../utils/localStorageWithExpiry';
import { checkAdvNationMapping } from '../utils/mapCurrencyNation';
import { submitInvoice } from '../utils/onSubmitInvoice';

import { orderSummaryTotalPriceAtom } from './AvailablePaymentMethods.ts/store';
import { isCpqaAtom } from './Cpqa/CpqaStore';
import { getIsCpqa } from './Cpqa/getCpqa';
import { savedCardInfoAtom } from './CreditCard/CreditCardStore';
import useCreditCardForm, {
  type UseCreditCardFormInput,
} from './CreditCard/useCreditCardForm';
import useSavedCreditCard, {
  type UseSavedCreditCardInput,
} from './CreditCard/useSavedCreditCard';
import useEWallet, { type UseEWalletInput } from './EWallet/useEWallet';
import { useErrorHandler } from './Errors/UseErrorHandler';
import {
  PaymentConfirmationError,
  PaymentGeneralError,
  PaymentActionRequiredError,
  OrderError,
  PaymentIntentExpire,
  PaymentDefaultError,
} from './Errors/customErrors';
import useFps, { type UseFpsInput } from './Fps/useFps';
import { internetBankAtom } from './Fpx/FpxStore';
import useFpx, { type UseFpxInput } from './Fpx/useFpx';
import useOnlineBanking, {
  type UseOnlineBankingInput,
} from './OnlineBanking/useOnlineBanking';
import usePay from './Pay/UsePay';
import usePayNowQr, { type UsePayNowQrInput } from './PayNowQr/usePayNowQr';
import usePayPal, { type UsePayPalInput } from './Paypal/usePayPal';
import usePromptPay, { type UsePromptPayInput } from './PromptPay/usePromptPay';
import useQris, { type UseQrisInput } from './Qris/useQris';
import useRedirectThirdParty from './RedirectThirdParty/useRedirectThirdParty';
import {
  allSurchargesAtom,
  selectedCardBrandAtom,
  surchargeAtom,
} from './Surcharges/SurchargeStore';
import { getSurcharges } from './Surcharges/getSurcharge';
import type { OnCompleteFromThirdParty, PaymentsCommon } from './usecaseTypes';

export interface Props extends PaymentsForCajaProps {
  children: (cajaUseCase: PaymentsCommon) => ReactNode;
}

const CajaUseCase = ({
  children,
  onError,
  onSuccess,
  advertiserNation,
  currency,
  orderId,
  interactionId,
  billToAgent,
  idempotencyKey,
  feature,
  paymentMethod: initialPaymentMethod,
  onPostAdButtonClicked,
  onPostAdButtonClickFailed,
  handledCancelPayment,
  onSaveCardOptionChanged,
  onPaymentMethodChanged,
  hideTabs = false,
  isAdBudgetSelected,
  postAdButtonText,
  previewButtonComponent,
  backButtonComponent,
  site,
  env,
  invoiceOnly,
  hideAllPayments,
  useCase,
  cartId = '',
  onGetSurchargeInfo,
}: // env,
Props) => {
  // UI states
  const [errorMessage, setErrorMessage] = useState<string | undefined>(() => {
    // getting stored value
    const saved = getWithExpiry('payments_error_message');
    if (!saved || saved === 'undefined') return undefined;
    return saved || undefined;
  });
  const [isPaymentInProgress, setIsPaymentInProgress] =
    useState<boolean>(false);
  const setAllSurcharges = useSetAtom(allSurchargesAtom);
  const surcharge = useAtomValue(surchargeAtom);
  const selectedCardBrand = useAtomValue(selectedCardBrandAtom);
  const [isCpqa, setIsCpqa] = useAtom(isCpqaAtom);
  const savedCardInfo = useAtomValue(savedCardInfoAtom);
  const internetBank = useAtomValue(internetBankAtom);
  const [orderSummaryTotal, setOrderSummaryTotal] = useAtom(
    orderSummaryTotalPriceAtom,
  );
  const safeCurrency = parseCurrency(currency);
  const [params] = useUrlSearchParams();
  const { generateNewIdempotencyKey } = useGenerateIdempotencyKey();
  const { features } = useFeatures();

  // eslint-disable-next-line prefer-const
  let paymentMethod =
    invoiceOnly || hideAllPayments || isCpqa ? 'Invoice' : initialPaymentMethod;

  useEffect(() => {
    setWithExpiry('payments_error_message', errorMessage, 1000 * 10);
  }, [errorMessage]);

  const {
    sendLinkSelectPaymentMethodEvent,
    sendLinkPostCajaAdOrUpgradeButtonEvent,
    sendViewExperimentsEvent,
  } = useAnalytics();

  const country = checkAdvNationMapping(advertiserNation, currency);

  const { t } = useTranslations(translations);

  // this will be triggered when payment widget is loaded
  const {
    isLoadingPaymentsData,
    orderSummary,
    contractBalances,
    hirerAccount,
    paymentsDataError,
  } = usePaymentsData(orderId, country);

  useEffect(() => {
    if (paymentsDataError) {
      onError(paymentsDataError, paymentMethod);
      return;
    }

    if (orderSummary && contractBalances && hirerAccount) {
      setAllSurcharges(getSurcharges(orderSummary));
      setIsCpqa(getIsCpqa(orderSummary));

      if (!savedCardInfo) {
        getSavedCards();
      }

      const isCajaUpsell = (isAdBudgetSelected && useCase === 'CAJA') || false;

      const paymentsViewData = generatePaymentsViewDataForCaja(
        orderSummary,
        contractBalances,
        hirerAccount,
        isCajaUpsell,
        getIsCpqa(orderSummary),
        country,
        features,
        useCase,
        invoiceOnly || false,
        hideAllPayments || false,
      );

      setOrderSummaryTotal(
        Number(paymentsViewData.widget_displayed_data.orderSummaryTotalPrice) ||
          0,
      );

      sendViewExperimentsEvent(
        features || [],
        country,
        isCajaUpsell,
        paymentsViewData.widget_displayed_data,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingPaymentsData]);

  // run only once upon loading is enough
  useEffect(() => {
    const isPaymentsReturn = checkIsPaymentsReturnUrl(params);

    // Reset isPaymentsInProgress state to false in CAJA
    if (!isPaymentsReturn) {
      handledCancelPayment();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!onGetSurchargeInfo) return;

    const isAmexWithSurcharge =
      surcharge !== 0 &&
      paymentMethod === 'CreditCard' &&
      selectedCardBrand === 'AMEX';

    onGetSurchargeInfo(selectedCardBrand, surcharge, isAmexWithSurcharge);
  }, [surcharge, selectedCardBrand, onGetSurchargeInfo, paymentMethod]);

  // mutation for invoices, this is the only mutation that is used and no other handling is required.
  // If we need to add more invoice related functions, create a new usecase file for invoices
  const [confirmInvoiceOrder] = useMutation(CONFIRM_INVOICE_ORDER, {
    context: {
      headers: {
        source: 'checkout',
        site: advertiserNation,
        'interaction-id': interactionId,
      },
    },
    variables: {
      input: {
        billToAgent,
        idempotencyKey,
        orderId,
      },
    },
  });

  // function to handle onComplete from third party.
  const onCompleteFromThirdParty: OnCompleteFromThirdParty = async ({
    status,
    stripeError,
    error,
  }) => {
    if (status === 'succeeded') {
      const result = await pay(() =>
        Promise.resolve({ status: 'succeeded', paymentMethod }),
      );

      if (result) {
        onSuccess(result);
      }
    }

    // do nothing if pending, show check status UI
    if (status === 'pending' || status === 'processing') {
      return;
    }

    if (stripeError) {
      return await pay(() => {
        throw new PaymentConfirmationError(
          stripeError.message ?? 'Stripe error',
          stripeError.type,
          stripeError.code,
          stripeError.decline_code,
        );
      });
    }

    if (error) {
      // handled errors for third party redirect only
      if (
        error instanceof PaymentGeneralError ||
        error instanceof PaymentConfirmationError ||
        error instanceof PaymentActionRequiredError ||
        error instanceof OrderError ||
        error instanceof PaymentIntentExpire
      ) {
        return await pay(() => {
          throw error;
        });
      }

      // unhandled errors
      return await pay(() => {
        throw new PaymentDefaultError(
          `onCompleteFromThirdParty unknown error: ${error}`,
        );
      });
    }

    return await pay(() =>
      Promise.resolve({
        status: (status as PaymentStatus) ?? 'canceled',
        paymentMethod,
      }),
    );
  };

  const onCompleteFromTestRecord = async ({
    status,
    paymentIntentId,
  }: HirerPaymentResult) => {
    // should do nothing when condition is met. This is used to handle test records
    if (
      status !== 'succeeded' ||
      paymentIntentId !== TEST_RECORD_SUCCEEDED_STATUS
    ) {
      return;
    }

    const result = {
      status: status as PaymentStatus,
      paymentIntentId,
      paymentMethod,
    };

    onSuccess(result);
  };

  const { handleError } = useErrorHandler({
    paymentMethod,
    setErrorMessage,
    onPostAdButtonClickFailed,
    onError,
    useCase,
  });

  const { redirectThirdParty } = useRedirectThirdParty({
    paymentMethod,
    setIsPaymentInProgress,
    handleError,
  });

  const { pay } = usePay({
    paymentMethod,
    handleError,
    setIsPaymentInProgress,
  });

  // general payment functions
  const handlePayAndPostButtonClick = async () => {
    try {
      // handle when button is pressed multiple times,
      // it should not do anything when payment in progress
      if (isPaymentInProgress) {
        return;
      }

      setIsPaymentInProgress(true);
      setErrorMessage('');

      await onPostAdButtonClicked({
        paymentMethod,
        isSavedCreditCard:
          paymentMethod === 'CreditCard' ? Boolean(savedCardInfo) : undefined,
        paymentMedium: getPaymentMedium([
          selectedBankingOption,
          selectedEWalletOption,
          internetBank,
        ]),
      });

      switch (paymentMethod) {
        // Payments with redirect
        case 'Fpx':
          sendLinkPostCajaAdOrUpgradeButtonEvent(paymentMethod, internetBank);
          const resultFpx = await redirectThirdParty(
            async () => await handleSubmit(),
          );
          if (resultFpx) {
            onCompleteFromTestRecord(resultFpx);
          }
          break;
        case 'IdOnlineBanking':
        case 'PhOnlineBanking':
        case 'ThOnlineBanking':
          sendLinkPostCajaAdOrUpgradeButtonEvent(
            paymentMethod,
            selectedBankingOption,
          );
          const resultOnlineBanking = await redirectThirdParty(
            async () => await submitOnlineBankingPayment(),
          );
          if (resultOnlineBanking) {
            onCompleteFromTestRecord(resultOnlineBanking);
          }
          break;
        case 'IdEWallet':
        case 'PhEWallet':
        case 'HkEWallet':
          sendLinkPostCajaAdOrUpgradeButtonEvent(
            paymentMethod,
            selectedEWalletOption,
          );
          const resultEWallet = await redirectThirdParty(
            async () => await submitEWalletPayment(),
          );
          if (resultEWallet) {
            onCompleteFromTestRecord(resultEWallet);
          }
          break;
        case 'PayPal':
          sendLinkPostCajaAdOrUpgradeButtonEvent(paymentMethod);
          const resultPayPal = await redirectThirdParty(
            async () => await submitPayPalPayment(),
          );
          if (resultPayPal) {
            onCompleteFromTestRecord(resultPayPal);
          }
          break;

        // Payments without redirect
        default:
          const result = await pay(async () => {
            if (invoiceOnly || hideAllPayments || isCpqa) {
              paymentMethod = 'Invoice';
            }
            sendLinkPostCajaAdOrUpgradeButtonEvent(paymentMethod);
            switch (paymentMethod) {
              case 'CreditCard':
                if (savedCardInfo) {
                  return await submitSavedCreditCardPayment();
                }
                return await submitCreditCardPayment();

              case 'PayNowQR':
                return await submitPayNowQrPayment();
              case 'Invoice':
                return await submitInvoice(confirmInvoiceOrder);

              case 'IdQr':
                return await submitQrisPayment();
              case 'PromptPay':
                return await submitPromptPayPayment();
              case 'HkFps':
                return await submitFpsPayment();
              default:
                return Promise.reject(
                  new PaymentGeneralError('Payment method not supported'),
                );
            }
          });

          if (result?.status === 'succeeded') {
            // return Contract when total price is 0
            if (orderSummaryTotal === 0) {
              result.paymentMethod = 'Contract';
            }
            onSuccess(result);
          }
          break;
      }
      setIsPaymentInProgress(false);
    } catch (error) {
      setIsPaymentInProgress(false);

      // handle errors from onPostAdButtonClicked (CAJA page)
      // this will include address input validation errors
      if (error instanceof Error) {
        onPostAdButtonClickFailed(error);
      }
    }
  };

  // Handle UI changes
  const onRadioChange = (
    selectedIndex: number,
    selectedItem?: string,
  ): void => {
    sendLinkSelectPaymentMethodEvent(selectedItem as PaymentMethodType);
    if (onPaymentMethodChanged) {
      generateNewIdempotencyKey();
      switch (selectedItem) {
        case 'CreditCard':
          onPaymentMethodChanged(selectedIndex, 'CreditCard');
          break;
        case 'Fpx':
          onPaymentMethodChanged(selectedIndex, 'Fpx');
          break;
        case 'Invoice':
          onPaymentMethodChanged(selectedIndex, 'Invoice');
          break;
        case 'IdOnlineBanking':
          onPaymentMethodChanged(selectedIndex, 'IdOnlineBanking');
          break;
        case 'IdEWallet':
          onPaymentMethodChanged(selectedIndex, 'IdEWallet');
          break;
        case 'IdQr':
          onPaymentMethodChanged(selectedIndex, 'IdQr');
          break;
        case 'PhOnlineBanking':
          onPaymentMethodChanged(selectedIndex, 'PhOnlineBanking');
          break;
        case 'PhEWallet':
          onPaymentMethodChanged(selectedIndex, 'PhEWallet');
          break;
        case 'ThOnlineBanking':
          onPaymentMethodChanged(selectedIndex, 'ThOnlineBanking');
          break;
        case 'PromptPay':
          onPaymentMethodChanged(selectedIndex, 'PromptPay');
          break;
        case 'PayNowQR':
          onPaymentMethodChanged(selectedIndex, 'PayNowQR');
          break;
        case 'HkEWallet':
          onPaymentMethodChanged(selectedIndex, 'HkEWallet');
          break;
        case 'PayPal':
          onPaymentMethodChanged(selectedIndex, 'PayPal');
          break;
        case 'HkFps':
          onPaymentMethodChanged(selectedIndex, 'HkFps');
          break;
        default:
          break;
      }
    }
  };

  // Use case section
  const useCreditCardFormInput: UseCreditCardFormInput<
    'ADBUDGET' | 'CAJA' | 'UPGRADE'
  > = {
    currency: safeCurrency,
    country,
    invoiceInputs: undefined,
    cajaInputs: {
      orderId,
      onSaveCardOptionChanged,
    },
    genericInputs: undefined,
  };

  const {
    onCreditCardFormChange,
    submitCreditCardPayment,
    isDisplaySurcharge,
  } = useCreditCardForm(useCreditCardFormInput);

  const useQrisInput: UseQrisInput<'ADBUDGET' | 'CAJA' | 'UPGRADE'> = {
    useCase,
    env,
    invoiceInputs: undefined,
    cajaInputs: {
      cartId,
      referenceId: orderId,
    },
  };

  const { submitQrisPayment, onCloseDialog } = useQris(useQrisInput);

  const usePayNowQrInput: UsePayNowQrInput<'ADBUDGET' | 'CAJA' | 'UPGRADE'> = {
    country,
    currency: safeCurrency,
    invoiceInputs: undefined,
    cajaInputs: { orderId },
  };

  const { submitPayNowQrPayment } = usePayNowQr(usePayNowQrInput);

  const usePromptPayInput: UsePromptPayInput<'ADBUDGET' | 'CAJA' | 'UPGRADE'> =
    {
      country,
      currency: safeCurrency,
      invoiceInputs: undefined,
      cajaInputs: { orderId },
    };

  const { submitPromptPayPayment } = usePromptPay(usePromptPayInput);

  const useSavedCreditCardInput: UseSavedCreditCardInput<'CAJA'> = {
    country,
    onError,
    paymentMethod,
    invoiceInputs: undefined,
    cajaInputs: {
      orderId,
    },
  };

  const {
    getSavedCards,
    submitSavedCreditCardPayment,
    onDeleteSavedCard,
    isGetSavedCardsLoading,
  } = useSavedCreditCard(useSavedCreditCardInput);

  const useFpxInputs: UseFpxInput<'CAJA'> = {
    useCase,
    invoiceInputs: undefined,
    cajaInputs: {
      cartId,
      referenceId: orderId,
    },
  };

  const { handleOnChangeInternetBank, handleSubmit } = useFpx(useFpxInputs);

  const useOnlineBankingInput: UseOnlineBankingInput<
    'ADBUDGET' | 'CAJA' | 'UPGRADE'
  > = {
    useCase,
    country,
    currency: safeCurrency,
    paymentMethod,
    env,
    invoiceInputs: undefined,
    cajaInputs: {
      cartId,
      referenceId: orderId,
    },
  };

  const {
    handleOnChangeOnlineBanking,
    selectedBankingOption,
    submitOnlineBankingPayment,
  } = useOnlineBanking(useOnlineBankingInput);

  const useEWalletInput: UseEWalletInput<'ADBUDGET' | 'CAJA' | 'UPGRADE'> = {
    currency: safeCurrency,
    useCase,
    country,
    paymentMethod,
    env,
    invoiceInputs: undefined,
    cajaInputs: { cartId, referenceId: orderId },
  };

  const {
    selectedEWalletOption,
    validEwallet,
    validAccNoOVO,
    eWalletsOption,
    submitEWalletPayment,
    handleOnChangeEWallet,
    accountNoOVO,
    handleOnChangeOVOEWallet,
  } = useEWallet(useEWalletInput);

  const usePayPalInput: UsePayPalInput<'ADBUDGET' | 'CAJA' | 'UPGRADE'> = {
    useCase,
    currency: safeCurrency,
    country,
    env,
    invoiceInputs: undefined,
    cajaInputs: { cartId, referenceId: orderId },
  };

  const { submitPayPalPayment } = usePayPal(usePayPalInput);

  const useFpsInput: UseFpsInput<'CAJA'> = {
    useCase,
    env,
    invoiceInputs: undefined,
    cajaInputs: {
      cartId,
      referenceId: orderId,
    },
  };

  const { submitFpsPayment, onCloseDialog: onCloseDialogFps } =
    useFps(useFpsInput);

  // Use case section ends here

  // common payment functions
  const creditCardForm: PaymentsCommon['creditCardForm'] = {
    onCreditCardFormChange,
    showSaveCard: true,
    isDisplaySurcharge,
  };

  const savedCreditCard: PaymentsCommon['savedCreditCard'] = {
    onDeleteSavedCard,
    isGetSavedCardsLoading,
  };

  const fpx: PaymentsCommon['fpx'] = {
    pay,
    handleOnChangeInternetBank,
  };

  const payNowQr: PaymentsCommon['payNowQr'] = {
    submitPayNowQrPayment,
  };

  const qris: PaymentsCommon['qris'] = {
    submitQrisPayment,
    onCloseDialog,
  };

  const promptPay: PaymentsCommon['promptPay'] = {
    submitPromptPayPayment,
  };

  const onlineBanking: PaymentsCommon['onlineBanking'] = {
    submitOnlineBankingPayment,
    handleOnChangeOnlineBanking,
  };

  const eWallet: PaymentsCommon['eWallet'] = {
    selectedEWalletOption,
    eWalletsOption,
    submitEWalletPayment,
    handleOnChangeEWallet,
    accountNoOVO,
    handleOnChangeOVOEWallet,
    validEwallet,
    validAccNoOVO,
  };

  const paypal: PaymentsCommon['paypal'] = {
    submitPayPalPayment,
  };

  const fps: PaymentsCommon['fps'] = {
    submitFpsPayment,
    onCloseDialog: onCloseDialogFps,
  };

  // common payment functions ends here
  return (
    <>
      {children({
        // UI states
        isPaymentInProgress,
        setIsPaymentInProgress,
        errorMessage,
        onRadioChange,

        // general payment functions
        handlePayAndPostButtonClick,
        onCompleteFromThirdParty,

        // utility
        // orderSummary, // Will be called upon page load but will not be imported anywhere else
        isOrderSummaryLoading: isLoadingPaymentsData,

        useCase,

        // From Props (either PaymentsForCajaProps or PaymentsForInvoiceProps)
        feature,
        paymentMethod,
        onPaymentMethodChanged,
        hideTabs,
        isAdBudgetSelected,
        postAdButtonText: postAdButtonText ?? t('pay.button.submit.caja'),
        previewButtonComponent,
        backButtonComponent,
        site,
        env,
        onError,
        country,
        currency: safeCurrency,
        invoiceOnly,
        cpqaOnly: isCpqa,
        hideAllPayments,
        orderId, // temporary, used on promptPay

        // Payment Methods
        creditCardForm,
        savedCreditCard,
        fpx,
        payNowQr,
        qris,
        promptPay,
        onlineBanking,
        eWallet,
        paypal,
        fps,
      })}
    </>
  );
};

export default CajaUseCase;
